import { useState } from 'react';
import { formatMoney, SIZE_SPECS, USAGE_SINCE, type ServerUsageResponse, type ServerView, type SizeChange, type UsageRun } from '@hearth/shared';
import { ApiError, type Api } from '../api';
import { useCached } from './cache';
import { errorText } from './serverWords';

// The Usage tab (M11, owners, members and admins), as the canvas board: what the server costs.
// While it's awake, how long and how much so far; then a month at a time (UTC months): the cost,
// hours awake, a bar a day, and each time it was awake.

/** "1 h 24 min", "39 min", "3 h". */
export function duration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** "2026-10" → "October 2026". */
export function monthName(month: string): string {
  return new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** The first month to show: when the server was created, never before usage was recorded. */
export function firstMonth(server: Pick<ServerView, 'createdAt'>): string {
  const created = server.createdAt?.slice(0, 7) ?? USAGE_SINCE;
  return created > USAGE_SINCE ? created : USAGE_SINCE;
}

/** The month before or after `month`. */
export function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1 + by, 1)).toISOString().slice(0, 7);
}

/** "Includes $0.50 for 1 size change." (none: empty). */
export function sizeChangesText(changes: readonly SizeChange[]): string {
  if (changes.length === 0) return '';
  const fees = formatMoney(changes.reduce((sum, c) => sum + c.amount, 0));
  return `Includes ${fees} for ${changes.length} size change${changes.length === 1 ? '' : 's'}.`;
}

/** The log's rows, newest first: each time it was awake, and each size change. */
type LogRow = { kind: 'run'; at: string; run: UsageRun } | { kind: 'resize'; at: string; change: SizeChange };
export function logRows(usage: Pick<ServerUsageResponse, 'runs' | 'sizeChanges'>): LogRow[] {
  return [
    ...usage.runs.map((run): LogRow => ({ kind: 'run', at: run.startedAt, run })),
    ...usage.sizeChanges.map((change): LogRow => ({ kind: 'resize', at: change.at, change })),
  ].sort((a, b) => b.at.localeCompare(a.at));
}

const who = (person: { name?: string; you: boolean } | undefined) => (person?.you ? 'You' : (person?.name ?? '—'));

const day = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const time = (iso: string) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

export function UsageTab({ api, server, onGone }: { api: Api; server: ServerView; onGone: () => void }) {
  const thisMonth = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(thisMonth);
  const [error, setError] = useState('');
  // Kept between tabs and months (cache.ts). A past month never changes, so it's read once; this
  // one is read again when it's old, and when the server wakes or sleeps.
  const current = month >= thisMonth;
  const usage = useCached(
    `usage:${server.serverId}:${month}`,
    async () => {
      const answer = await api.get<ServerUsageResponse>(`/v1/servers/${encodeURIComponent(server.serverId)}/usage?month=${month}`);
      setError('');
      return answer;
    },
    {
      version: current ? server.status : '',
      freshMs: current ? undefined : Infinity,
      failed: (err) => (err instanceof ApiError && err.status === 404 ? onGone() : setError(errorText(err))),
    },
  ).data;

  return (
    <>
      {error && <p className="notice" role="alert">{error}</p>}
      <UsagePanel usage={usage?.month === month ? usage : undefined} month={month} latest={month >= thisMonth} earliest={month <= firstMonth(server)} onMonth={(by) => setMonth(shiftMonth(month, by))} />
    </>
  );
}

/** The tab, given the month's usage (no fetching: easy to test). */
export function UsagePanel(props: { usage: ServerUsageResponse | undefined; month: string; latest: boolean; earliest: boolean; onMonth: (by: number) => void }) {
  const { usage, month, latest, earliest, onMonth } = props;
  const awake = usage?.runs.find((r) => r.stoppedAt === undefined);
  const top = Math.max(1, ...(usage?.days ?? []));
  return (
    <div className="usage">
      {latest && awake && (
        <p className="usage-live" aria-live="polite">
          <span className="usage-dot" aria-hidden="true" />
          {`Awake for ${duration(awake.seconds)} · about ${formatMoney(awake.cost)} so far · ${SIZE_SPECS[awake.size].name}${awake.pricePerHour === undefined ? '' : `, ${formatMoney(awake.pricePerHour)} an hour`}`}
        </p>
      )}
      <section className="panel">
        <div className="usage-head">
          <h2>{monthName(month)}</h2>
          <div className="usage-months">
            {!earliest && <button className="btn btn-small" type="button" aria-label="Previous month" onClick={() => onMonth(-1)}>‹</button>}
            {!latest && <button className="btn btn-small" type="button" aria-label="Next month" onClick={() => onMonth(1)}>›</button>}
          </div>
        </div>
        {!usage ? (
          <p className="quiet" aria-live="polite">Loading…</p>
        ) : (
          <>
            <div className="usage-stats">
              <div className="usage-total">
                <span className="quiet">Cost</span>
                <strong>{formatMoney(usage.total)}</strong>
              </div>
              <div>
                <span className="quiet">Awake</span>
                <strong>{duration(usage.awakeSeconds)}</strong>
              </div>
            </div>
            <div className="usage-bars" aria-hidden="true">
              {usage.days.map((seconds, i) => (
                <span key={i} className={seconds ? undefined : 'none'} style={seconds ? { height: `${Math.max(4, Math.round((seconds / top) * 100))}%` } : undefined} />
              ))}
            </div>
            <p className="usage-axis quiet">
              <span>1</span>
              <span>Hours awake each day</span>
              <span>{usage.days.length}</span>
            </p>
            {usage.createFee !== undefined && <p className="quiet">{`Includes ${formatMoney(usage.createFee)} to create it.`}</p>}
            {usage.sizeChanges.length > 0 && <p className="quiet">{sizeChangesText(usage.sizeChanges)}</p>}
          </>
        )}
      </section>
      {usage && (
        <section className="panel">
          <h2>Server Awake Log</h2>
          {usage.runs.length === 0 && usage.sizeChanges.length === 0 ? (
            <p className="quiet">It wasn't awake this month.</p>
          ) : (
            <div className="scroll">
              <table className="usage-log">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Woke</th>
                    <th>Fell asleep</th>
                    <th>Woken by</th>
                    <th>Size</th>
                    <th className="num">Awake</th>
                    <th className="num">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {logRows(usage).map((row) =>
                    row.kind === 'run' ? (
                      <tr key={`run ${row.at}`}>
                        <td>{day(row.at)}</td>
                        <td>{time(row.at)}</td>
                        <td>{row.run.stoppedAt ? time(row.run.stoppedAt) : 'awake now'}</td>
                        <td>{who(row.run.wokenBy)}</td>
                        <td>{SIZE_SPECS[row.run.size].name}</td>
                        <td className="num">{duration(row.run.seconds)}</td>
                        <td className="num">{formatMoney(row.run.cost)}</td>
                      </tr>
                    ) : (
                      <tr key={`resize ${row.at}`} className="usage-fee">
                        <td>{day(row.at)}</td>
                        <td colSpan={2}>{`Size changed at ${time(row.at)}`}</td>
                        <td>{who(row.change.by)}</td>
                        <td>{`${SIZE_SPECS[row.change.from].name} → ${SIZE_SPECS[row.change.to].name}`}</td>
                        <td className="num">—</td>
                        <td className="num">{formatMoney(row.change.amount)}</td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
